import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type { ExtensionListItem } from '@kirin/api-client/contracts/extensions';
import { ORG_EXTENSION_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/extensions';
import { severityOptionsDescending } from '@kirin/api-client/contracts/shared/severity';
import { NameCell, IDECell } from '../DataTableExtensionsTab/CellRenderers';
import { UsersCell, PublisherDisplay } from './CellRenderers';
import { SourceCell } from '@/components/shared/extensions';
import { SecuritySummaryCell } from '@/components/shared/extensions/SecuritySummaryCell';
import { RiskEstimateCell } from '@/components/shared/extensions/RiskEstimateCell';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { LastSeenFreshnessCell } from '@/components/shared/LastSeenFreshnessCell';
import {
  ExtensionSourceFilterOption,
  ExtensionIDEFilterOption,
  RiskEstimateFilterOption,
} from '../DataTableExtensionsTab/FilterComponents';
import { UserFilterOption, PublisherFilterOption } from './FilterComponents';
import { SeverityFilterOption } from '@/components/DataTable/alerts/FilterComponents';
import {
  ORG_EXTENSION_FILTER_URL_KEYS,
  EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
  EXTENSION_SOURCE_OPTIONS,
} from './constants';
import {
  fetchOrgExtensionFilterOptions,
  getOrgExtensionFilterOptionsQueryKey,
  fetchOrgExtensionUserFilterOptions,
  getOrgExtensionUserFilterOptionsQueryKey,
} from './api';

const EmptyOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...EmptyOperators,
];

const StringOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
  ...EmptyOperators,
];

const EnumOperators = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...EmptyOperators,
];

/**
 * Column definitions for the org extensions DataTable.
 * organizationId is passed as a parameter so filter option fetchers
 * always use the correct org scope — never stale from a previous render.
 *
 * Column order: Name, Source, Publisher, IDE, Users, Risk, First Seen, Last Seen
 *
 * `canFilterByUser` (owner/manager only) gates the Users column's filter: its
 * dropdown options come from the org members list, an endpoint RBAC-gated to
 * owner/manager. For members the column stays visible and sortable but is
 * non-filterable, so the members fetch (→ 403) never fires (KIR-3266). The
 * per-row Users cell + modal stay available to everyone — that endpoint is
 * member-scoped (returns the member's own data), not RBAC-gated.
 */
export function createOrgExtensionColumns(
  organizationId: string,
  onUserClick?: (extension: ExtensionListItem) => void,
  canFilterByUser: boolean = false,
  capabilityDetectionEnabled: boolean = false,
  agentmeshRiskEnabled: boolean = false
): ColumnConfig<ExtensionListItem>[] {
  // Users column: filterable (member-backed dropdown) for owner/manager,
  // otherwise sortable-but-non-filterable with no member fetch.
  const usersColumn: ColumnConfig<ExtensionListItem> = canFilterByUser
    ? {
        id: ORG_EXTENSION_FILTERABLE_FIELDS.USER,
        header: 'Users',
        accessor: 'userCount',
        width: 100,
        renderCell: ({ row }) => (
          <UsersCell row={row} onClick={() => onUserClick?.(row)} />
        ),
        sortable: true,
        sortField: 'userCount',
        sortKey: 'uc',
        filterable: true,
        dataType: ColumnDataTypes.ENUM,
        filterOperators: EnumOperators,
        statePersistence: {
          urlKey:
            ORG_EXTENSION_FILTER_URL_KEYS[ORG_EXTENSION_FILTERABLE_FIELDS.USER],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params =>
            getOrgExtensionUserFilterOptionsQueryKey(organizationId, {
              search: params.search,
              limit: params.limit,
            }),
          queryFn: params =>
            fetchOrgExtensionUserFilterOptions(organizationId, params),
          queryConfig: { staleTime: 2 * 60 * 1000 },
          render: (option, isSelected) => (
            <UserFilterOption
              userId={option.value}
              userName={(option.metadata?.name as string) ?? null}
              userEmail={(option.metadata?.email as string) ?? null}
              isSelected={isSelected}
            />
          ),
        },
      }
    : {
        id: ORG_EXTENSION_FILTERABLE_FIELDS.USER,
        header: 'Users',
        accessor: 'userCount',
        width: 100,
        renderCell: ({ row }) => (
          <UsersCell row={row} onClick={() => onUserClick?.(row)} />
        ),
        sortable: true,
        sortField: 'userCount',
        sortKey: 'uc',
        filterable: false,
      };

  const securityColumn: ColumnConfig<ExtensionListItem> = {
    id: ORG_EXTENSION_FILTERABLE_FIELDS.SECURITY,
    header: 'Risk',
    accessor: 'securitySummary',
    width: 150,
    renderCell: ({ row }) => (
      <SecuritySummaryCell summary={row.securitySummary} />
    ),
    sortable: true,
    sortField: 'security',
    sortKey: 'sec',
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: EnumOperators,
    statePersistence: {
      urlKey:
        ORG_EXTENSION_FILTER_URL_KEYS[ORG_EXTENSION_FILTERABLE_FIELDS.SECURITY],
    },
    filterOptions: {
      type: FilterOptionTypes.STATIC,
      options: severityOptionsDescending,
      render: (option, isSelected) => (
        <SeverityFilterOption severity={option.value} isSelected={isSelected} />
      ),
    },
  };

  // Risk Estimate (agentmesh_risk_estimate, KIR-1928): the overall AgentMesh
  // score, styled like the Alerts severity column. Multiselect filter options
  // are server-backed (distinct labels present in the org's inventory) so
  // AgentMesh vocabulary changes flow through automatically.
  const riskEstimateColumn: ColumnConfig<ExtensionListItem> = {
    id: ORG_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE,
    header: 'Risk',
    accessor: 'riskEstimate',
    width: 150,
    renderCell: ({ row }) => (
      <RiskEstimateCell riskEstimate={row.riskEstimate} />
    ),
    sortable: true,
    sortField: 'riskEstimate',
    sortKey: 'risk',
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: EnumOperators,
    statePersistence: {
      urlKey:
        ORG_EXTENSION_FILTER_URL_KEYS[
          ORG_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE
        ],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params =>
        getOrgExtensionFilterOptionsQueryKey(
          organizationId,
          ORG_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE,
          { search: params.search, limit: params.limit }
        ),
      queryFn: params =>
        fetchOrgExtensionFilterOptions(
          organizationId,
          ORG_EXTENSION_FILTERABLE_FIELDS.RISK_ESTIMATE,
          params
        ),
      queryConfig: { staleTime: 2 * 60 * 1000 },
      pagination: {
        mode: 'offset',
        limit: EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
      },
      render: (option, isSelected) => (
        <RiskEstimateFilterOption
          value={option.value}
          isSelected={isSelected}
        />
      ),
    },
  };

  return [
    // 1. Name column
    {
      id: ORG_EXTENSION_FILTERABLE_FIELDS.NAME,
      header: 'Name',
      accessor: 'extensionName',
      width: 200,
      renderCell: ({ row }) => <NameCell row={row} />,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          ORG_EXTENSION_FILTER_URL_KEYS[ORG_EXTENSION_FILTERABLE_FIELDS.NAME],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getOrgExtensionFilterOptionsQueryKey(
            organizationId,
            ORG_EXTENSION_FILTERABLE_FIELDS.NAME,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchOrgExtensionFilterOptions(
            organizationId,
            ORG_EXTENSION_FILTERABLE_FIELDS.NAME,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: {
          mode: 'offset',
          limit: EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
        },
      },
    },
    // 2. Source column (matches legacy position)
    {
      id: ORG_EXTENSION_FILTERABLE_FIELDS.SOURCE,
      header: 'Source',
      accessor: 'isBuiltIn',
      width: 110,
      renderCell: ({ row }) => (
        <SourceCell source={row.isBuiltIn ? 'built-in' : 'added'} />
      ),
      sortable: true,
      sortField: 'source',
      sortKey: 'src',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          ORG_EXTENSION_FILTER_URL_KEYS[ORG_EXTENSION_FILTERABLE_FIELDS.SOURCE],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: EXTENSION_SOURCE_OPTIONS,
        render: (option, isSelected) => (
          <ExtensionSourceFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    // 3. Publisher column - matches legacy: VerifiedPublisherIcon + plain text (no muted-foreground)
    {
      id: ORG_EXTENSION_FILTERABLE_FIELDS.PUBLISHER,
      header: 'Publisher',
      accessor: 'publisher',
      width: 130,
      renderCell: ({ row }) => (
        <PublisherDisplay name={row.publisher} isVerified={row.isVerified} />
      ),
      sortable: true,
      sortField: 'publisher',
      sortKey: 'pub',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperators,
      statePersistence: {
        urlKey:
          ORG_EXTENSION_FILTER_URL_KEYS[
            ORG_EXTENSION_FILTERABLE_FIELDS.PUBLISHER
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getOrgExtensionFilterOptionsQueryKey(
            organizationId,
            ORG_EXTENSION_FILTERABLE_FIELDS.PUBLISHER,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchOrgExtensionFilterOptions(
            organizationId,
            ORG_EXTENSION_FILTERABLE_FIELDS.PUBLISHER,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: {
          mode: 'offset',
          limit: EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
        },
        render: (option, isSelected) => (
          <PublisherFilterOption
            publisherName={option.label ?? option.value}
            isVerified={(option.metadata?.isVerified as boolean) ?? false}
            isSelected={isSelected}
          />
        ),
      },
    },
    // 4. IDE column
    {
      id: ORG_EXTENSION_FILTERABLE_FIELDS.IDE,
      header: 'IDE',
      accessor: 'sources',
      width: 200,
      renderCell: ({ row }) => <IDECell row={row} />,
      sortable: true,
      sortField: 'ide',
      sortKey: 'ide',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperators,
      statePersistence: {
        urlKey:
          ORG_EXTENSION_FILTER_URL_KEYS[ORG_EXTENSION_FILTERABLE_FIELDS.IDE],
      },
      // Server-backed so the dropdown reflects the org's actual inventory
      // instead of the released platform list (KIR-2733).
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          getOrgExtensionFilterOptionsQueryKey(
            organizationId,
            ORG_EXTENSION_FILTERABLE_FIELDS.IDE,
            { search: params.search, limit: params.limit }
          ),
        queryFn: params =>
          fetchOrgExtensionFilterOptions(
            organizationId,
            ORG_EXTENSION_FILTERABLE_FIELDS.IDE,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        pagination: {
          mode: 'offset',
          limit: EXTENSION_FILTER_OPTIONS_PAGE_SIZE,
        },
        render: (option, isSelected) => (
          <ExtensionIDEFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    // 5. Users column
    usersColumn,
    // 6. Risk — while agentmesh_risk_estimate is on, the AgentMesh estimate
    // REPLACES the capability-detection severity in this slot (KIR-1928).
    ...(agentmeshRiskEnabled
      ? [riskEstimateColumn]
      : capabilityDetectionEnabled
        ? [securityColumn]
        : []),
    // 7. First Seen
    {
      id: ORG_EXTENSION_FILTERABLE_FIELDS.FIRST_SEEN_AT,
      header: 'First Seen',
      accessor: 'firstSeenAt',
      width: 130,
      renderCell: ({ row }) => (
        <span className="text-muted-foreground">
          <RelativeTimeWithTooltip date={row.firstSeenAt} />
        </span>
      ),
      sortable: true,
      sortField: 'firstSeenAt',
      sortKey: 'fs',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          ORG_EXTENSION_FILTER_URL_KEYS[
            ORG_EXTENSION_FILTERABLE_FIELDS.FIRST_SEEN_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    // 8. Last Seen
    {
      id: ORG_EXTENSION_FILTERABLE_FIELDS.LAST_SEEN_AT,
      header: 'Last Seen',
      accessor: 'lastSeenAt',
      width: 130,
      renderCell: ({ row }) => (
        <LastSeenFreshnessCell lastSeenAt={row.lastSeenAt} />
      ),
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'ls',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey:
          ORG_EXTENSION_FILTER_URL_KEYS[
            ORG_EXTENSION_FILTERABLE_FIELDS.LAST_SEEN_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
